import { supabase } from "../lib/supabase";
import type { Database } from "../types/database";
import { userService } from "./userService";

// Cache for organizations to avoid redundant database calls
const organizationCache = new Map<string, any>();

// Define types for organization settings
export interface AssetNumberingSettings {
  prefix: string;
  suffix: string;
  starting_number: number;
  current_number: number;
  padding_length?: number; // Added for padding
}

export interface OrganizationSettings {
  asset_numbering?: AssetNumberingSettings;
  default_currency?: string;
  default_asset_status?: string;
  default_warranty_days?: number;
}

export const organizationService = {
  /**
   * Get an organization by ID
   * @param organizationId The organization ID
   * @returns The organization or null if not found
   */
  async getOrganization(organizationId: string) {
    // Check cache first
    if (organizationCache.has(organizationId)) {
      return organizationCache.get(organizationId);
    }

    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", organizationId)
        .single();

      if (error) {
        console.error("Error fetching organization:", error);
        return null;
      }

      // Cache the result
      organizationCache.set(organizationId, data);
      return data;
    } catch (error) {
      console.error("Error in getOrganization:", error);
      return null;
    }
  },

  /**
   * Get organizations for a user
   * @param userId The user ID
   * @returns Array of organizations the user is a member of
   */
  async getUserOrganizations(userId: string) {
    try {
      const { data, error } = await supabase
        .from("organization_members")
        .select(
          `
          organization_id,
          role,
          organizations (*)
        `
        )
        .eq("user_id", userId);

      if (error) {
        console.error("Error fetching user organizations:", error);
        return [];
      }

      // Update cache with fetched organizations
      data.forEach((item) => {
        if (item.organizations) {
          organizationCache.set(item.organization_id, item.organizations);
        }
      });

      return data;
    } catch (error) {
      console.error("Error in getUserOrganizations:", error);
      return [];
    }
  },

  /**
   * Create a new organization
   * @param organization The organization data
   * @param userId The user ID creating the organization
   * @returns The created organization
   */
  async createOrganization(
    organization: Omit<
      Database["public"]["Tables"]["organizations"]["Insert"],
      "created_by"
    >,
    userId: string
  ) {
    try {
      // First create the organization
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .insert({
          ...organization,
          created_by: userId,
          updated_by: userId, // Ensure updated_by is set
        })
        .select()
        .single();

      if (orgError) {
        console.error("Error creating organization:", orgError);
        throw orgError;
      }

      // Then add the creator as an owner
      const { error: memberError } = await supabase
        .from("organization_members")
        .insert({
          organization_id: orgData.id,
          user_id: userId,
          role: "owner",
        });

      if (memberError) {
        console.error("Error adding organization member:", memberError);
        throw memberError;
      }

      // Update the user's current organization
      await userService.updateCurrentOrganization(userId, orgData.id);

      // Update cache
      organizationCache.set(orgData.id, orgData);

      return orgData;
    } catch (error) {
      console.error("Error in createOrganization:", error);
      throw error;
    }
  },

  /**
   * Delete an organization
   * @param organizationId The organization ID to delete
   * @param userId The user ID requesting the deletion (must be an owner)
   * @returns Success status
   */
  async deleteOrganization(organizationId: string, userId: string) {
    try {
      // First check if the user is an owner of the organization
      const { data: memberData, error: memberError } = await supabase
        .from("organization_members")
        .select("role")
        .eq("organization_id", organizationId)
        .eq("user_id", userId)
        .single();

      if (memberError || !memberData) {
        console.error("Error checking membership:", memberError);
        throw new Error("You are not a member of this organization");
      }

      if (memberData.role !== "owner") {
        throw new Error("Only organization owners can delete an organization");
      }

      // Delete all related data
      // 1. Delete maintenance records associated with the organization
      const { error: maintenanceError } = await supabase
        .from("maintenance_records")
        .delete()
        .eq("organization_id", organizationId);

      if (maintenanceError) {
        console.error("Error deleting maintenance records:", maintenanceError);
        throw maintenanceError;
      }

      // 2. Delete checkout records associated with the organization
      const { error: checkoutError } = await supabase
        .from("checkout_records")
        .delete()
        .eq("organization_id", organizationId);

      if (checkoutError) {
        console.error("Error deleting checkout records:", checkoutError);
        throw checkoutError;
      }

      // 3. Delete documents associated with the organization
      const { error: documentsError } = await supabase
        .from("documents")
        .delete()
        .eq("organization_id", organizationId);

      if (documentsError) {
        console.error("Error deleting documents:", documentsError);
        throw documentsError;
      }

      // 4. Delete assets associated with the organization
      const { error: assetsError } = await supabase
        .from("assets")
        .delete()
        .eq("organization_id", organizationId);

      if (assetsError) {
        console.error("Error deleting assets:", assetsError);
        throw assetsError;
      }

      // 5. Delete inventory items associated with the organization
      const { error: inventoryError } = await supabase
        .from("inventory_items")
        .delete()
        .eq("organization_id", organizationId);

      if (inventoryError) {
        console.error("Error deleting inventory items:", inventoryError);
        throw inventoryError;
      }

      // 6. Delete organization invitations
      const { error: invitationsError } = await supabase
        .from("organization_invitations")
        .delete()
        .eq("organization_id", organizationId);

      if (invitationsError) {
        console.error(
          "Error deleting organization invitations:",
          invitationsError
        );
        throw invitationsError;
      }

      // 7. Delete organization members
      const { error: membersError } = await supabase
        .from("organization_members")
        .delete()
        .eq("organization_id", organizationId);

      if (membersError) {
        console.error("Error deleting organization members:", membersError);
        throw membersError;
      }

      // 8. Update user profiles that have this as current_organization_id
      const { error: profilesError } = await supabase
        .from("profiles")
        .update({ current_organization_id: null })
        .eq("current_organization_id", organizationId);

      if (profilesError) {
        console.error("Error updating user profiles:", profilesError);
        throw profilesError;
      }

      // 9. Delete the organization itself
      const { error: deleteError } = await supabase
        .from("organizations")
        .delete()
        .eq("id", organizationId);

      if (deleteError) {
        console.error("Error deleting organization:", deleteError);
        throw deleteError;
      }

      // Remove from cache
      organizationCache.delete(organizationId);

      return true;
    } catch (error) {
      console.error("Error in deleteOrganization:", error);
      throw error;
    }
  },

  /**
   * Update an organization
   * @param organizationId The organization ID
   * @param updates The updates to apply
   * @returns The updated organization
   */
  async updateOrganization(
    organizationId: string,
    updates: Database["public"]["Tables"]["organizations"]["Update"]
  ) {
    try {
      const { data, error } = await supabase
        .from("organizations")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", organizationId)
        .select()
        .single();

      if (error) {
        console.error("Error updating organization:", error);
        throw error;
      }

      // Update cache
      organizationCache.set(organizationId, data);

      return data;
    } catch (error) {
      console.error("Error in updateOrganization:", error);
      throw error;
    }
  },

  /**
   * Get members of an organization
   * @param organizationId The organization ID
   * @returns Array of members with their profiles
   */
  async getOrganizationMembers(organizationId: string) {
    try {
      // First get the organization members
      const { data: members, error: membersError } = await supabase
        .from("organization_members")
        .select("*")
        .eq("organization_id", organizationId);

      if (membersError) {
        console.error("Error fetching organization members:", membersError);
        return [];
      }

      // Then get the profiles for each member
      if (members && members.length > 0) {
        const userIds = members.map((member) => member.user_id);

        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("*")
          .in("id", userIds);

        if (profilesError) {
          console.error("Error fetching member profiles:", profilesError);
          return members;
        }

        // Combine the data
        const membersWithProfiles = members.map((member) => {
          const profile = profiles?.find((p) => p.id === member.user_id);
          return {
            ...member,
            profiles: profile || null,
          };
        });

        return membersWithProfiles;
      }

      return members || [];
    } catch (error) {
      console.error("Error in getOrganizationMembers:", error);
      return [];
    }
  },

  /**
   * Invite a user to an organization
   * @param email The email of the user to invite
   * @param organizationId The organization ID
   * @param role The role to assign
   * @returns Success status
   */
  async inviteUser(
    email: string,
    organizationId: string,
    role: "admin" | "member"
  ) {
    try {
      // First check if the current user's email matches the invite email
      const { data: currentUser } = await supabase.auth.getUser();

      if (currentUser?.user?.email?.toLowerCase() === email.toLowerCase()) {
        // Check if the user is already a member of the organization
        const { data: existingMember } = await supabase
          .from("organization_members")
          .select("id")
          .eq("organization_id", organizationId)
          .eq("user_id", currentUser.user.id)
          .maybeSingle();

        if (existingMember) {
          throw new Error("You are already a member of this organization");
        }

        // Check if there's already a pending invitation
        const { data: existingInvitation } = await supabase
          .from("organization_invitations")
          .select("id, status")
          .eq("organization_id", organizationId)
          .eq("user_id", currentUser.user.id)
          .maybeSingle();

        if (existingInvitation) {
          if (existingInvitation.status === "pending") {
            throw new Error(
              "You already have a pending invitation to this organization"
            );
          } else if (existingInvitation.status === "declined") {
            // If previously declined, update the invitation to pending
            const { error: updateError } = await supabase
              .from("organization_invitations")
              .update({
                status: "pending",
                role,
                updated_at: new Date().toISOString(),
              })
              .eq("id", existingInvitation.id);

            if (updateError) {
              console.error("Error updating invitation:", updateError);
              throw updateError;
            }
            return true;
          }
        }

        // Create a new invitation for the current user
        const { error: inviteError } = await supabase
          .from("organization_invitations")
          .insert({
            organization_id: organizationId,
            user_id: currentUser.user.id,
            role,
            status: "pending",
          });

        if (inviteError) {
          console.error("Error creating invitation:", inviteError);
          throw inviteError;
        }

        return true;
      }

      // Try to find the user by email in the profiles table
      const { data: userProfile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", email.toLowerCase())
        .maybeSingle();

      if (profileError) {
        console.error("Error finding user profile:", profileError);
        throw new Error("Error finding user");
      }

      if (!userProfile) {
        throw new Error("User not found. Please ask them to sign up first.");
      }

      // Check if the user is already a member of the organization
      const { data: existingMember } = await supabase
        .from("organization_members")
        .select("id")
        .eq("organization_id", organizationId)
        .eq("user_id", userProfile.id)
        .maybeSingle();

      if (existingMember) {
        throw new Error("This user is already a member of the organization");
      }

      // Check if there's already a pending invitation
      const { data: existingInvitation } = await supabase
        .from("organization_invitations")
        .select("id, status")
        .eq("organization_id", organizationId)
        .eq("user_id", userProfile.id)
        .maybeSingle();

      if (existingInvitation) {
        if (existingInvitation.status === "pending") {
          throw new Error("This user already has a pending invitation");
        } else if (existingInvitation.status === "declined") {
          // If previously declined, update the invitation to pending
          const { error: updateError } = await supabase
            .from("organization_invitations")
            .update({
              status: "pending",
              role,
              updated_at: new Date().toISOString(),
            })
            .eq("id", existingInvitation.id);

          if (updateError) {
            console.error("Error updating invitation:", updateError);
            throw updateError;
          }
          return true;
        }
      }

      // Create a new invitation
      const { error: inviteError } = await supabase
        .from("organization_invitations")
        .insert({
          organization_id: organizationId,
          user_id: userProfile.id,
          role,
          status: "pending",
        });

      if (inviteError) {
        console.error("Error creating invitation:", inviteError);
        throw inviteError;
      }

      return true;
    } catch (error) {
      console.error("Error in inviteUser:", error);
      throw error;
    }
  },

  /**
   * Update a member's role in an organization
   * @param organizationId The organization ID
   * @param userId The user ID
   * @param role The new role
   * @returns Success status
   */
  async updateMemberRole(
    organizationId: string,
    userId: string,
    role: "owner" | "admin" | "member"
  ) {
    try {
      const { error } = await supabase
        .from("organization_members")
        .update({ role })
        .match({ organization_id: organizationId, user_id: userId });

      if (error) {
        console.error("Error updating member role:", error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error("Error in updateMemberRole:", error);
      throw error;
    }
  },

  /**
   * Remove a member from an organization
   * @param organizationId The organization ID
   * @param userId The user ID to remove
   * @returns Success status
   */
  async removeMember(organizationId: string, userId: string) {
    try {
      const { error } = await supabase
        .from("organization_members")
        .delete()
        .match({ organization_id: organizationId, user_id: userId });

      if (error) {
        console.error("Error removing member:", error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error("Error in removeMember:", error);
      throw error;
    }
  },

  /**
   * Upload organization logo
   * @param organizationId The organization ID
   * @param file The logo file
   * @returns The updated organization
   */
  async uploadLogo(organizationId: string, _file: File) {
    // TODO: Add logo upload logic later
    console.warn("Logo upload not implemented yet");
    return this.updateOrganization(organizationId, {
      logo_url: "", // placeholder
    });
  },

  /**
   * Clear the organization cache
   */
  clearCache() {
    organizationCache.clear();
  },

  /**
   * Get pending invitations for a user
   * @param userId The user ID
   * @returns Array of pending invitations with organization details
   */
  async getUserInvitations(userId: string) {
    try {
      const { data, error } = await supabase
        .from("organization_invitations")
        .select(
          `
          *,
          organizations (*)
        `
        )
        .eq("user_id", userId)
        .eq("status", "pending");

      if (error) {
        console.error("Error fetching user invitations:", error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error("Error in getUserInvitations:", error);
      return [];
    }
  },

  /**
   * Accept an organization invitation
   * @param invitationId The invitation ID
   * @returns Success status
   */
  async acceptInvitation(invitationId: string) {
    try {
      // First get the invitation details
      const { data: invitation, error: inviteError } = await supabase
        .from("organization_invitations")
        .select("*")
        .eq("id", invitationId)
        .single();

      if (inviteError || !invitation) {
        console.error("Error fetching invitation:", inviteError);
        throw new Error("Invitation not found");
      }

      // Check if the invitation is still pending
      if (invitation.status !== "pending") {
        throw new Error("This invitation is no longer valid");
      }

      // Start a transaction by using a callback with RLS policies
      // First, add the user to the organization
      const { error: memberError } = await supabase
        .from("organization_members")
        .insert({
          organization_id: invitation.organization_id,
          user_id: invitation.user_id,
          role: invitation.role,
        });

      if (memberError) {
        console.error("Error adding organization member:", memberError);
        throw memberError;
      }

      // Then update the invitation status
      const { error: updateError } = await supabase
        .from("organization_invitations")
        .update({
          status: "accepted",
          updated_at: new Date().toISOString(),
        })
        .eq("id", invitationId);

      if (updateError) {
        console.error("Error updating invitation:", updateError);
        throw updateError;
      }

      return true;
    } catch (error) {
      console.error("Error in acceptInvitation:", error);
      throw error;
    }
  },

  /**
   * Decline an organization invitation
   * @param invitationId The invitation ID
   * @returns Success status
   */
  async declineInvitation(invitationId: string) {
    try {
      const { error } = await supabase
        .from("organization_invitations")
        .update({
          status: "declined",
          updated_at: new Date().toISOString(),
        })
        .eq("id", invitationId);

      if (error) {
        console.error("Error declining invitation:", error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error("Error in declineInvitation:", error);
      throw error;
    }
  },

  /**
   * Get organization settings
   * @param organizationId The organization ID
   * @returns The organization settings or default settings if not found
   */
  async getOrganizationSettings(
    organizationId: string
  ): Promise<OrganizationSettings> {
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("settings")
        .eq("id", organizationId)
        .single();

      if (error) {
        console.error("Error fetching organization settings:", error);
        return this.getDefaultSettings();
      }

      // Return the settings or default if not set
      return data?.settings || this.getDefaultSettings();
    } catch (error) {
      console.error("Error in getOrganizationSettings:", error);
      return this.getDefaultSettings();
    }
  },

  /**
   * Update organization settings
   * @param organizationId The organization ID
   * @param settings The settings to update
   * @returns Success status
   */
  async updateOrganizationSettings(
    organizationId: string,
    settings: OrganizationSettings
  ): Promise<boolean> {
    try {
      // First get current settings to merge
      const currentSettings = await this.getOrganizationSettings(
        organizationId
      );

      // Merge with new settings
      const mergedSettings = {
        ...currentSettings,
        ...settings,
      };

      // Update in database
      const { error } = await supabase
        .from("organizations")
        .update({
          settings: mergedSettings,
          updated_at: new Date().toISOString(),
        })
        .eq("id", organizationId);

      if (error) {
        console.error("Error updating organization settings:", error);
        return false;
      }

      // Update cache
      const cachedOrg = organizationCache.get(organizationId);
      if (cachedOrg) {
        cachedOrg.settings = mergedSettings;
        organizationCache.set(organizationId, cachedOrg);
      }

      return true;
    } catch (error) {
      console.error("Error in updateOrganizationSettings:", error);
      return false;
    }
  },

  /**
   * Get default organization settings
   * @returns Default organization settings
   */
  getDefaultSettings(): OrganizationSettings {
    return {
      asset_numbering: {
        prefix: "KITRA",
        suffix: "",
        starting_number: 1000,
        current_number: 1000,
        padding_length: 0, // 0 means no padding
      },
      default_currency: "GBP",
      default_asset_status: "available",
      default_warranty_days: 365,
    };
  },

  /**
   * Generate the next asset barcode based on organization settings
   * @param organizationId The organization ID
   * @returns The next asset barcode and increments the counter
   */
  async getNextAssetBarcode(organizationId: string): Promise<string> {
    try {
      // Get current settings
      const settings = await this.getOrganizationSettings(organizationId);

      // Ensure asset_numbering exists
      if (!settings.asset_numbering) {
        settings.asset_numbering = this.getDefaultSettings().asset_numbering!;
      }

      // Keep track of attempts to avoid infinite loops
      let attempts = 0;
      const maxAttempts = 100;
      let barcode = "";
      let isUnique = false;

      // Loop until we find a unique barcode or reach max attempts
      while (!isUnique && attempts < maxAttempts) {
        attempts++;

        // Generate barcode
        const {
          prefix,
          suffix,
          current_number,
          padding_length = 0,
        } = settings.asset_numbering;

        // Apply padding if specified (convert to string and pad with leading zeros)
        let formattedNumber = current_number.toString();
        if (padding_length > 0) {
          formattedNumber = formattedNumber.padStart(padding_length, "0");
        }

        // Construct the barcode with prefix and suffix if provided
        barcode = `${prefix ? prefix + "-" : ""}${formattedNumber}${
          suffix ? "-" + suffix : ""
        }`;

        // Check if barcode already exists
        const { data, error } = await supabase
          .from("assets")
          .select("id")
          .eq("barcode", barcode)
          .eq("organization_id", organizationId)
          .limit(1);

        if (error) {
          console.error("Error checking barcode uniqueness:", error);
          throw error;
        }

        // If no data returned, barcode is unique
        isUnique = !data || data.length === 0;

        // Increment counter for next attempt if needed
        settings.asset_numbering.current_number = current_number + 1;
      }

      if (!isUnique) {
        throw new Error(
          `Could not generate unique barcode after ${maxAttempts} attempts`
        );
      }

      // Update settings with the new counter value
      await this.updateOrganizationSettings(organizationId, settings);

      return barcode;
    } catch (error) {
      console.error("Error generating asset barcode:", error);
      // Fallback to a timestamp-based barcode if there's an error
      return `ASSET-${Date.now()}`;
    }
  },
};
